import { useState, FormEvent } from "react";

export default function FinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea[name='message']") as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      alert("Message must be 500 characters or less.");
      return;
    }
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d70n8ppqa83q0hmd1d90", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden py-0">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/8b94d1be4bea21c5d71aa43623403a76.jpg"
          alt="Luxury vehicle close-up"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/75 to-black/90"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left – Text */}
          <div>
            <p className="font-sans text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-4">
              Get Started Today
            </p>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Protect Your Vehicle
              <br />
              <em className="text-gold not-italic">The Right Way.</em>
            </h2>
            <p className="font-sans text-[#B8B8B8] text-base leading-relaxed mb-10">
              Limited availability — secure your indoor storage space today. Contact us to check current availability at our San Rafael or Point Richmond facility.
            </p>

            {/* Contact Options */}
            <div className="space-y-4">
              <a
                href="tel:4154539180"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 rounded-full group-hover:bg-gold/10 transition-colors">
                  <i className="ri-phone-line text-gold text-lg"></i>
                </div>
                <div>
                  <p className="font-sans text-white font-medium text-sm">Office Line</p>
                  <p className="font-sans text-gold text-base font-semibold">(415) 453-9180</p>
                </div>
              </a>
              <a
                href="tel:4158464281"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 rounded-full group-hover:bg-gold/10 transition-colors">
                  <i className="ri-smartphone-line text-gold text-lg"></i>
                </div>
                <div>
                  <p className="font-sans text-white font-medium text-sm">Cell — Jim Long</p>
                  <p className="font-sans text-gold text-base font-semibold">(415) 846-4281</p>
                </div>
              </a>
              <a
                href="mailto:doncollinsstorage@gmail.com"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 rounded-full group-hover:bg-gold/10 transition-colors">
                  <i className="ri-mail-line text-gold text-lg"></i>
                </div>
                <div>
                  <p className="font-sans text-white font-medium text-sm">Email</p>
                  <p className="font-sans text-gold text-sm">doncollinsstorage@gmail.com</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right – Lead Form */}
          <div className="bg-charcoal-200/90 backdrop-blur-sm rounded-2xl border border-white/10 p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-gold text-3xl"></i>
                </div>
                <h3 className="font-serif text-white text-2xl font-semibold mb-3">
                  Request Received!
                </h3>
                <p className="font-sans text-[#B8B8B8] text-sm leading-relaxed">
                  Thank you for reaching out. We&apos;ll contact you shortly to confirm availability and next steps.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-white text-2xl font-semibold mb-2">
                  Request Availability
                </h3>
                <p className="font-sans text-[#9A9A9A] text-sm mb-6">
                  Fill out the form and we&apos;ll get back to you within 24 hours.
                </p>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-sans text-xs text-[#9A9A9A] uppercase tracking-wide block mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full bg-charcoal-400 border border-white/10 rounded text-white text-sm px-4 py-3 placeholder-[#555] focus:outline-none focus:border-gold/50 transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs text-[#9A9A9A] uppercase tracking-wide block mb-1.5">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full bg-charcoal-400 border border-white/10 rounded text-white text-sm px-4 py-3 placeholder-[#555] focus:outline-none focus:border-gold/50 transition-colors"
                        placeholder="(415) 555-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-xs text-[#9A9A9A] uppercase tracking-wide block mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full bg-charcoal-400 border border-white/10 rounded text-white text-sm px-4 py-3 placeholder-[#555] focus:outline-none focus:border-gold/50 transition-colors"
                      placeholder="you@email.com"
                    />
                  </div>

                  <div>
                    <label className="font-sans text-xs text-[#9A9A9A] uppercase tracking-wide block mb-1.5">
                      Vehicle Type *
                    </label>
                    <select
                      name="vehicle_type"
                      required
                      className="w-full bg-charcoal-400 border border-white/10 rounded text-white text-sm px-4 py-3 focus:outline-none focus:border-gold/50 transition-colors cursor-pointer appearance-none"
                    >
                      <option value="" className="bg-charcoal-200">Select vehicle type</option>
                      <option value="Car / Automobile" className="bg-charcoal-200">Car / Automobile</option>
                      <option value="Classic / Collector Car" className="bg-charcoal-200">Classic / Collector Car</option>
                      <option value="Motorcycle" className="bg-charcoal-200">Motorcycle</option>
                      <option value="RV / Motorhome" className="bg-charcoal-200">RV / Motorhome</option>
                      <option value="Boat" className="bg-charcoal-200">Boat</option>
                      <option value="Truck / Equipment" className="bg-charcoal-200">Truck / Equipment</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-sans text-xs text-[#9A9A9A] uppercase tracking-wide block mb-1.5">
                      Storage Duration
                    </label>
                    <select
                      name="duration"
                      className="w-full bg-charcoal-400 border border-white/10 rounded text-white text-sm px-4 py-3 focus:outline-none focus:border-gold/50 transition-colors cursor-pointer appearance-none"
                    >
                      <option value="" className="bg-charcoal-200">Select duration</option>
                      <option value="1-3 months" className="bg-charcoal-200">1–3 months</option>
                      <option value="3-6 months" className="bg-charcoal-200">3–6 months</option>
                      <option value="6-12 months" className="bg-charcoal-200">6–12 months</option>
                      <option value="1+ years" className="bg-charcoal-200">1+ year (long-term)</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-sans text-xs text-[#9A9A9A] uppercase tracking-wide block mb-1.5">
                      Additional Notes
                    </label>
                    <textarea
                      name="message"
                      maxLength={500}
                      rows={3}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-charcoal-400 border border-white/10 rounded text-white text-sm px-4 py-3 placeholder-[#555] focus:outline-none focus:border-gold/50 transition-colors resize-none"
                      placeholder="Vehicle make, model, year, special requirements..."
                    ></textarea>
                    <p className={`font-sans text-xs mt-1 text-right ${charCount > 450 ? "text-gold" : "text-[#666]"}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full font-sans text-sm font-semibold bg-gold text-charcoal py-4 rounded tracking-wide hover:bg-gold-light transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {loading ? "Sending..." : "Send Inquiry"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
